/**
 * Résolution des erreurs d'authentification en message affichable,
 * dans la langue active de l'interface.
 *
 * Les services d'auth lancent des clés i18n (ex: "errors.auth.invalidCredentials").
 * Sans traduction, l'UI affichait la clé brute à l'utilisateur.
 */

import i18n from "@/lib/i18n/i18n";
import {
  getSupabaseErrorKey,
  getSupabaseErrorMessage,
} from "@/lib/utils/supabase-error-messages";

const I18N_ERROR_KEY_PATTERN = /^errors\.[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;

/** Indique si un message lancé par un service est une clé i18n et non un texte utilisateur. */
export const isErrorI18nKey = (message: string): boolean =>
  I18N_ERROR_KEY_PATTERN.test(message);

const extractRawMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const { message } = error as { message?: string };
    return message ?? "";
  }
  return "";
};

/**
 * Retourne la clé i18n correspondant à une erreur d'authentification.
 * Utile pour distinguer les cas (identifiants invalides, email non vérifié, ...)
 * sans dépendre de la langue affichée.
 */
export const resolveAuthErrorKey = (
  error: unknown,
  fallbackKey = "errors.auth.loginFailed"
): string => {
  const rawMessage = extractRawMessage(error);
  if (isErrorI18nKey(rawMessage)) return rawMessage;
  return getSupabaseErrorKey(error, fallbackKey);
};

/**
 * Retourne un message d'erreur traduit dans la langue active.
 * Accepte aussi bien une erreur Supabase brute qu'une clé i18n lancée par un service.
 */
export const getLocalizedAuthErrorMessage = (
  error: unknown,
  fallbackKey = "errors.auth.loginFailed"
): string => {
  const resolvedKey = resolveAuthErrorKey(error, fallbackKey);
  if (i18n.exists(resolvedKey)) return i18n.t(resolvedKey);

  const fallbackMessage = i18n.exists(fallbackKey)
    ? i18n.t(fallbackKey)
    : i18n.t("errors.generic");

  // La clé résolue n'existe pas : ne jamais afficher une clé brute à l'utilisateur.
  if (isErrorI18nKey(extractRawMessage(error))) return fallbackMessage;

  return getSupabaseErrorMessage(error, fallbackMessage);
};
